import type { MeResponse } from '@nolens/domain';
import { Button, Dialog, useLocale } from '@nolens/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { detectExtension, openPanelOnExtension } from '../lib/extension';
import { macLinked, openMac } from '../lib/mac';
import { macText } from '../i18n/mac';
import { shellText } from '../i18n/shell';

/** Estado da extensão: em falta, ligada a outra conta (ou a nenhuma), ou pronta. */
export function useExtensionState(email: string | null, enabled = true) {
  const q = useQuery({
    queryKey: ['extension', 'shell'],
    queryFn: () => detectExtension(),
    staleTime: 60_000,
    enabled,
  });
  const state = q.isPending
    ? null
    : !q.data
      ? ('missing' as const)
      : q.data.linkedEmail !== email
        ? ('unlinked' as const)
        : ('ready' as const);
  return state;
}

/**
 * «Gravar» quando a barra lateral não abriu (AppShell): a gravação faz-se na extensão. Diz o
 * que fazer conforme a extensão: instalar, ligar ou clicar no ícone (uma versão que ainda não
 * abre a barra lateral a partir da app).
 */
export function RecordDialog({
  me,
  open,
  onClose,
}: {
  me: MeResponse;
  open: boolean;
  onClose: () => void;
}) {
  const locale = useLocale();
  const t = shellText(locale);
  const tm = macText(locale);
  const state = useExtensionState(me.user.email, open);
  // Com a app de Mac ligada (ADR-G25), «Gravar» pergunta primeiro onde: no Chrome ou no Mac
  const [chrome, setChrome] = useState(false);
  const close = () => {
    setChrome(false);
    onClose();
  };
  if (macLinked(me) && !chrome)
    return (
      <Dialog open={open} onClose={close} title={t.recordTitle} size="sm" closeLabel={t.close}>
        <div className="grid gap-3 text-[15px]" data-record-dialog="choose">
          <p className="text-text-2">{tm.recordWhere}</p>
          <button
            type="button"
            className="rounded-[12px] p-4 text-left shadow-[inset_0_0_0_1px_var(--border)] hover:bg-surface-2"
            data-record-mac
            onClick={() => {
              openMac('gravar');
              close();
            }}
          >
            <span className="block font-medium">{tm.recordOnMac}</span>
            <span className="block text-sm text-text-2">{tm.recordOnMacHint}</span>
          </button>
          <button
            type="button"
            className="rounded-[12px] p-4 text-left shadow-[inset_0_0_0_1px_var(--border)] hover:bg-surface-2"
            data-record-chrome
            onClick={() => {
              // No próprio clique: o Chrome só abre a barra lateral em resposta a um gesto
              if (state !== 'ready') return setChrome(true);
              void openPanelOnExtension().then((ok) => (ok ? close() : setChrome(true)));
            }}
          >
            <span className="block font-medium">{tm.recordInChrome}</span>
            <span className="block text-sm text-text-2">{tm.recordInChromeHint}</span>
          </button>
        </div>
      </Dialog>
    );
  return (
    <Dialog open={open} onClose={close} title={t.recordTitle} size="sm" closeLabel={t.close}>
      <div className="grid gap-4 text-[15px]" data-record-dialog={state ?? 'checking'}>
        {state === null && <p className="text-text-2">{t.recordChecking}</p>}
        {state === 'missing' && (
          <>
            <p className="text-text-2">{t.recordMissing}</p>
            {me.extensionUrl ? (
              <a href={me.extensionUrl} target="_blank" rel="noopener noreferrer">
                <Button fullWidth>{t.installExtension}</Button>
              </a>
            ) : (
              <Link to="/extensao" onClick={close}>
                <Button fullWidth>{t.installExtension}</Button>
              </Link>
            )}
          </>
        )}
        {state === 'unlinked' && (
          <>
            <p className="text-text-2">{t.recordUnlinked}</p>
            <Link to="/extensao?ligar=1" onClick={close}>
              <Button fullWidth>{t.connectExtension}</Button>
            </Link>
          </>
        )}
        {state === 'ready' && (
          <ol className="grid gap-3">
            {[t.recordStep1, t.recordStep2, t.recordStep3].map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[13px] font-semibold text-accent-text">
                  {i + 1}
                </span>
                <span className="text-text-2">{s}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Dialog>
  );
}
